import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import {
  IChecklistTemplateResponse,
  IChecklistTemplate,
  IChecklistInstance,
  IChecklistInstanceResponse,
  ICreateChecklistInstancePayload,
  IUpdateChecklistResponsePayload,
  IConfirmChecklistInstancePayload,
  IImplementationTimeline,
  IImplementationTimelineResponse,
} from '@/types/checklist.types';

export const useGetActiveChecklistTemplate = (tenantId: string | number | undefined) => {
  return useQuery({
    queryKey: ['checklist-template', 'active', tenantId],
    queryFn: async (): Promise<IChecklistTemplate | null> => {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      try {
        const response = await axiosInstance.get<IChecklistTemplateResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-templates/active`,
        );

        return response.data.data;
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof error.response === 'object' &&
          error.response !== null &&
          'status' in error.response &&
          error.response.status === 404
        ) {
          return null;
        }
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'status' in error.response &&
        error.response.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useGetChecklistInstanceByTenant = (tenantId: string | number | undefined) => {
  return useQuery({
    queryKey: ['checklist-instance', 'tenant', tenantId],
    queryFn: async (): Promise<IChecklistInstance | null> => {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      try {
        const response = await axiosInstance.get<{ data: { items: IChecklistInstance[] } }>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances?page=1&page_size=1`,
        );

        const instances = response.data.data?.items || [];
        if (instances.length === 0) {
          return null;
        }

        const instance = instances[0];

        if (instance.template?.sections) {
          instance.template.sections.sort((a, b) => a.order_index - b.order_index);

          instance.template.sections.forEach(section => {
            section.sub_sections?.sort((a, b) => a.order_index - b.order_index);
            section.sub_sections?.forEach(subSection => {
              subSection.items?.sort((a, b) => a.order_index - b.order_index);
            });
          });
        }

        return instance;
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof error.response === 'object' &&
          error.response !== null &&
          'status' in error.response &&
          error.response.status === 404
        ) {
          return null;
        }
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'status' in error.response &&
        error.response.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useGetChecklistInstanceByCompany = (
  tenantId: string | number | undefined,
  companyDataId: number | undefined,
) => {
  return useQuery({
    queryKey: ['checklist-instance', 'company', tenantId, companyDataId],
    queryFn: async (): Promise<IChecklistInstance | null> => {
      if (!tenantId || !companyDataId) {
        throw new Error('Tenant ID and Company Data ID are required');
      }

      try {
        const response = await axiosInstance.get<IChecklistInstanceResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/company/${companyDataId}`,
        );

        const instance = response.data.data;

        if (instance.template?.sections) {
          instance.template.sections.sort((a, b) => a.order_index - b.order_index);

          instance.template.sections.forEach(section => {
            section.sub_sections?.sort((a, b) => a.order_index - b.order_index);
            section.sub_sections?.forEach(subSection => {
              subSection.items?.sort((a, b) => a.order_index - b.order_index);
            });
          });
        }

        return instance;
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof error.response === 'object' &&
          error.response !== null &&
          'status' in error.response &&
          error.response.status === 404
        ) {
          toast.info('Checklist instance has not been created yet. Creating a new one...', {
            autoClose: 3000,
          });
          return null;
        }
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!companyDataId,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'status' in error.response &&
        error.response.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useCreateChecklistInstance = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({
      tenantId,
      payload,
    }: {
      tenantId: string | number;
      payload: ICreateChecklistInstancePayload;
    }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances`;
      const { data } = await axiosInstance.post<IChecklistInstanceResponse>(url, payload);
      return data.data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['checklist-instance']);
      queryClient.invalidateQueries(['implementation-timeline']);
      await refetchCompletionStatus();
      toast.success('Checklist instance berhasil dibuat');
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useUpdateChecklistResponse = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({
      tenantId,
      responseId,
      payload,
    }: {
      tenantId: string | number;
      responseId: number;
      payload: IUpdateChecklistResponsePayload;
    }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-responses/${responseId}`;
      const { data } = await axiosInstance.put(url, payload);
      return data;
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries(['checklist-instance']);
      queryClient.invalidateQueries(['implementation-timeline']);
      await refetchCompletionStatus();
      toast.success('Berhasil diperbarui');
    },
    onError: (error: unknown) => {
      let errorMessage = 'Failed to update checklist response';
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        'message' in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast.error(errorMessage, {
        autoClose: 4000,
      });
    },
  });
};

export const useCompleteChecklistResponse = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, responseId }: { tenantId: string | number; responseId: number }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-responses/${responseId}/complete`;
      const { data } = await axiosInstance.put(url);
      return data;
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries(['checklist-instance']);
      queryClient.invalidateQueries(['implementation-timeline']);
      await refetchCompletionStatus();
      toast.success('Berhasil diselesaikan');
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useConfirmChecklistInstance = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({
      tenantId,
      instanceId,
      companyDataId,
      payload,
    }: {
      tenantId: string | number;
      instanceId: number;
      companyDataId: number;
      payload: IConfirmChecklistInstancePayload;
    }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/confirm`;
      const { data } = await axiosInstance.put<{ success: boolean; message: string }>(url, payload);
      return { ...data, companyDataId };
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['checklist-instance']);
      queryClient.invalidateQueries(['implementation-timeline']);
      await refetchCompletionStatus();
      toast.success('Konfirmasi checklist berhasil disimpan');
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useStartImplementation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      instanceId,
      companyDataId,
      implementationStartedAt,
    }: {
      tenantId: string | number;
      instanceId: number;
      companyDataId: number;
      // null = reset, undefined = today (server default), string = chosen date (YYYY-MM-DD)
      implementationStartedAt?: string | null;
    }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/start-implementation`;
      const payload =
        implementationStartedAt === undefined
          ? { implementation_started_at: new Date().toISOString().slice(0, 10) }
          : { implementation_started_at: implementationStartedAt };
      await axiosInstance.put(url, payload);
      return { companyDataId };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['checklist-instance']);
      queryClient.invalidateQueries(['implementation-timeline']);
      toast.success(
        variables.implementationStartedAt === null ? 'Tanggal implementasi direset' : 'Implementasi dimulai',
      );
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useImplementationTimeline = (
  tenantId: string | number | undefined,
  instanceId: number | undefined,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['implementation-timeline', tenantId, instanceId],
    queryFn: async (): Promise<IImplementationTimeline> => {
      if (!tenantId || !instanceId) {
        throw new Error('Tenant ID and Instance ID are required');
      }
      const { data } = await axiosInstance.get<IImplementationTimelineResponse>(
        `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/implementation-timeline`,
      );
      return data.data;
    },
    enabled: enabled && !!tenantId && !!instanceId,
    staleTime: 60 * 1000,
  });
};
